import { useCallback, useContext, useRef } from 'react';
import moment from 'moment';
import {
  ColDef,
  GridApi,
  ProcessHeaderForExportParams,
} from 'ag-grid-community';

import { toCsv } from 'components/DataTable/exportCells';

import CardContext from '../../../contexts/CardContext';
import GridContext from '../../../contexts/GridContext';
import getComparison from '../../V5Gadget/Matrix/getComparison';
import { HISTOGRAM_CELL_RENDERER } from '../../SingleMetricDateMatrix/SingleMetricDateMatrixContent/constants';
import isSingleMetricDateMatrix from '../../../types/visTypeCheckers/isSingleMetricDateMatrix';
import dateRangeInputToAmericanLabel from '../../../dateRangeInputToAmericanLabel';
import delay from '../../../delay';
import { getProgressCellText } from '../../ProgressCellRenderer';
import { getContractProgressCellText } from '../../ContractProgressCell';
import { DATE_CELL, GEO_POINT_CELL } from '../constants';
import useDateScope from '../../../hooks/useDateScope';
import useValueFormatters from 'hooks/useValueFormatters';

interface FleetOpsGridColDef extends ColDef {
  cellRendererParams:
    | {
        fieldView?: FleetOps.BaseViewField;
        cell?: BoardProgressCell;
      }
    | undefined;
}

export const processCellCallback =
  (
    formatValue: ({
      formatting,
      value,
    }: {
      formatting: MetricFormatting;
      value: number;
    }) => string,
    formatField: ({
      dataset,
      field,
      value,
    }: {
      dataset: string;
      field: string;
      value: string | number | null | undefined | number[];
    }) => string | number,
  ) =>
  // AG's ProcessCellForExportParams, or DataTable's equivalent
  (params: DataTable.ExportCellParams) => {
    const colDef = params.column.getUserProvidedColDef() as
      | FleetOpsGridColDef
      | undefined;
    if (!colDef) {
      return '';
    }
    const cellRenderer = colDef.cellRenderer;
    const cellParams = colDef.cellRendererParams;

    if (cellRenderer === 'progressCell' && params.node) {
      if (!cellParams) {
        return '';
      }
      if (!cellParams.cell) {
        return '';
      }

      const cell = cellParams.cell as BoardProgressCell;
      const fieldView = cellParams.fieldView as
        | FleetOps.BaseViewField
        | undefined;
      return getProgressCellText(
        params.node.data,
        cell,
        formatValue,
        fieldView,
      );
    }

    if (cellRenderer === 'contractProgressCell' && params.node) {
      return getContractProgressCellText(params.node.data, formatValue);
    }

    if (
      cellParams &&
      cellRenderer === DATE_CELL &&
      cellParams.fieldView &&
      cellParams.fieldView.dateFormat
    ) {
      if (params.value) {
        return moment.utc(params.value).format(cellParams.fieldView.dateFormat);
      }
      return '';
    }

    if (cellParams && cellRenderer === GEO_POINT_CELL && cellParams.fieldView) {
      return formatField({
        value: params.value,
        field: cellParams.fieldView.field,
        dataset: cellParams.fieldView.dataset,
      });
    }

    if (params.value !== undefined && Array.isArray(params.value)) {
      return params.value.join(', ');
    }

    if (typeof params.value === 'number') {
      if (
        cellRenderer === 'currencyCell' ||
        cellRenderer === 'bonusRewardCell'
      ) {
        const formatted = formatValue({
          value: params.value,
          formatting: {
            precision: 2,
          },
        });
        return formatted;
      }
    }

    if (params.value && typeof params.value === 'object') {
      const { value, formatting, type } = params.value;
      if (!params.value) {
        return '-';
      }

      const precision = formatting ? formatting.precision : 2;
      const prefix = formatting ? formatting.prefix : undefined;
      const postfix = formatting ? formatting.postfix : undefined;

      const updatedFormatting = { precision, prefix, postfix };

      const formatted = formatValue({ value, formatting: updatedFormatting });
      const previous =
        cellRenderer === HISTOGRAM_CELL_RENDERER
          ? params.value.comparison
          : params.value.previous;
      if (type === 'percentDelta' || type === 'delta') {
        if (previous === 0) {
          return type === 'percentDelta' ? '' : formatted;
        }
        const { delta } = getComparison({
          value: value as number,
          previous: previous as number,
          formatting,
          type,
        });
        if (delta === Infinity) {
          return '';
        }

        if (delta === null || delta === undefined) {
          return '-';
        }

        if (delta >= 0) {
          return `+ ${formatValue({ value: delta, formatting })}`;
        } else if (prefix !== 'currency') {
          return `- ${formatValue({ value: Math.abs(delta), formatting })}`;
        }

        return `${formatValue({ value: delta, formatting })}`;
      } else {
        return formatted;
      }
    }

    if (typeof params.value === 'string') {
      return params.value.replace(
        /[^a-zA-Z0-9, !@#$%^&*()\-=_+`.;:'"?|[\]\\/{}]/g,
        ' ',
      );
    }
    return params.value;
  };

// A Single Metric Date Matrix's export header: its date columns are labelled
// with their date (or date range) instead of the metric's name. The interval
// and range are read from a row's cell in that column.
const getSingleMetricDateMatrixHeader = ({
  colDef,
  sampleRow,
}: {
  colDef: ColDef;
  sampleRow: DataTable.Row | undefined;
}) => {
  if (colDef.headerName === 'Chg') {
    return 'Chg';
  }

  if (colDef.cellRenderer === 'groupingCellRenderer') {
    return colDef.field || '';
  }

  if (!colDef.field) {
    return '';
  }

  const cell = sampleRow ? sampleRow[colDef.field] : undefined;
  const interval = cell ? (cell.interval as string | undefined) : undefined;
  const valueDateRange = cell
    ? (cell.valueDateRange as DateRangeInput | undefined)
    : undefined;
  if (interval && valueDateRange) {
    if (interval === 'day') {
      return moment.utc(colDef.field).format("ddd, MMM DD'YY");
    }
    return dateRangeInputToAmericanLabel(valueDateRange);
  }
  return colDef.field;
};

// AG's processHeaderCallback. The sample is the row with id '1' (the second
// row given to the grid), as it always has been.
export const processSingleMetricDateMatrixHeaderCallback = (
  params: Pick<ProcessHeaderForExportParams, 'api' | 'column'>,
) => {
  const sampleNode = params.api.getRowNode('1');
  return getSingleMetricDateMatrixHeader({
    colDef: params.column.getColDef(),
    sampleRow: sampleNode ? sampleNode.data : undefined,
  });
};

// DataTable's ExportHandle processHeader. The sample is the first exported
// row with a cell in the column, so a one-row matrix gets its dates too.
export const processSingleMetricDateMatrixExportHeader: DataTable.ProcessExportHeader =
  ({ column, rows }) => {
    const colDef = column.getColDef();
    const { field } = colDef;
    return getSingleMetricDateMatrixHeader({
      colDef,
      sampleRow: field ? rows.find((row) => !!row[field]) : undefined,
    });
  };

// Encapsulates the CSV generation for a grid gadget (ag-grid `getDataAsCsv`),
// including the filename derivation and the cell/header formatting callbacks.
// Extracted from ExportButton so it can be reused by GridCsvExportRegistrar,
// which registers a matrix gadget's CSV during headless scheduled-report export
// without the export ContextMenu ever being opened.
const useGridCsvExport = ({
  overrideGridApi,
  exportHandle,
  exportName,
}: {
  overrideGridApi?: GridApi;
  // A DataTable's rows instead of a grid
  exportHandle?: DataTable.ExportHandle;
  exportName?: string;
}) => {
  const { i, chartDefinition } = useContext(CardContext);
  const { gridApi, dataType } = useContext(GridContext);
  const dateScope = useDateScope({});
  const { formatValue, formatField } = useValueFormatters();
  const domId = `export-button-${i}`;

  const getFileName = useCallback(() => {
    const name = exportName ? exportName : dataType;
    const prefix = `FleetOps ${name} export`;
    const postfix = `${dateScope.startDate} - ${dateScope.endDate}`;
    const fileName = `${prefix} - ${postfix}`.replaceAll('.', '');
    return fileName;
  }, [dataType, dateScope.endDate, dateScope.startDate, exportName]);

  // Read when exporting, as the AG path reads the grid then: a callback
  // registered before the table's last update (e.g. its totals arriving
  // after its rows) still exports what the table shows
  const exportHandleRef = useRef(exportHandle);
  exportHandleRef.current = exportHandle;

  const exportDataAsCsv = useCallback(async (): Promise<ExcelFileData> => {
    const handle = exportHandleRef.current;
    if (handle) {
      // Built from the table's rows, so there's nothing to wait for
      const fileName = getFileName();
      const csv = toCsv(
        handle.getCells({
          processCell: processCellCallback(formatValue, formatField),
          processHeader: isSingleMetricDateMatrix(chartDefinition)
            ? processSingleMetricDateMatrixExportHeader
            : undefined,
          isTotalsRowIncluded: true,
        }),
      );
      return { content: csv.replace(/€/g, ''), fileName };
    }
    if (!gridApi && !overrideGridApi) {
      throw new Error('Grid API missing');
    }
    const gridApiToUse = overrideGridApi
      ? overrideGridApi
      : (gridApi as GridApi);

    const buttonDom = document.getElementById(domId);
    if (buttonDom) {
      buttonDom.scrollIntoView(true);
      await delay(5000);
    }

    const fileName = getFileName();
    const csv = gridApiToUse.getDataAsCsv({
      fileName,
      processCellCallback: processCellCallback(formatValue, formatField),
      processHeaderCallback: isSingleMetricDateMatrix(chartDefinition)
        ? processSingleMetricDateMatrixHeaderCallback
        : undefined,
    });

    if (csv) {
      return {
        content: csv.replace(/€/g, ''),
        fileName,
      };
    } else {
      window.alert('Something went wrong!');
      throw new Error(`Failed to export ${fileName}`);
    }
  }, [
    gridApi,
    overrideGridApi,
    domId,
    getFileName,
    formatValue,
    formatField,
    chartDefinition,
  ]);

  return { exportDataAsCsv, getFileName, domId };
};

export default useGridCsvExport;
